import React from "react";
import AppLayout from "@/components/AppLayout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CSV_REPORTS = [
  { entity: "leads",         label: "Leads",              desc: "All lead records with status, division and value." },
  { entity: "contacts",      label: "Contacts",           desc: "Master contact records with tags and division." },
  { entity: "opportunities", label: "Opportunities",      desc: "Pipeline with stage, value and probability." },
  { entity: "tasks",         label: "Tasks",              desc: "Assignments, priorities, due dates and status." },
  { entity: "enrolments",    label: "Academy enrolments", desc: "Student enrolments, statuses and placement outcomes." },
  { entity: "applications",  label: "Study Abroad + MBBS",desc: "Applications with country, university and intake." },
  { entity: "wealth",        label: "Wealth enquiries",   desc: "Enquiries with product, amount, status and ack." },
  { entity: "projects",      label: "Digital projects",   desc: "Projects with status and budget." },
];

async function download(url, filename) {
  const token = sessionStorage.getItem("miro_token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default function Reports() {
  const dlCsv = async (entity) => {
    try { await download(`${API}/reports/${entity}.csv`, `miro_${entity}.csv`); toast.success(`${entity}.csv downloaded`); }
    catch (e) { toast.error("Download failed"); }
  };
  const dlPdf = async () => {
    try { await download(`${API}/reports/summary.pdf`, `miro_summary.pdf`); toast.success("Summary PDF downloaded"); }
    catch (e) { toast.error("Download failed"); }
  };

  return (
    <AppLayout title="Reports">
      <div className="rounded-2xl p-6 text-white relative overflow-hidden miro-grain mb-5"
           style={{ background: "var(--miro-navy)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(400px at 90% 20%, rgba(14,165,255,0.35), transparent 60%)" }}/>
        <div className="relative z-10 flex items-center gap-6 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="miro-overline text-[var(--miro-blue)]">Executive summary</div>
            <h2 className="font-display font-black text-3xl mt-2 tracking-tight">Branded MIRO One report</h2>
            <p className="text-sm text-white/70 mt-2 max-w-md">
              A single-page executive PDF with KPIs, funnel and division breakdown — ready to email to leadership.
            </p>
          </div>
          <Button data-testid="download-summary-pdf-btn" onClick={dlPdf}
                  className="h-11 font-semibold text-[var(--miro-navy)] bg-white hover:bg-white/90">
            <FileText size={16} className="mr-2"/> Download PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {CSV_REPORTS.map(r => (
          <div key={r.entity} data-testid={`report-${r.entity}`} className="miro-card p-5 flex flex-col">
            <div className="flex items-start justify-between">
              <div>
                <div className="miro-overline">CSV export</div>
                <h3 className="font-display font-bold text-lg text-[var(--miro-navy)] mt-1">{r.label}</h3>
              </div>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#0EA5FF18", color: "#0284C7" }}>
                <Sparkles size={17}/>
              </div>
            </div>
            <p className="text-sm text-[var(--miro-text-secondary)] mt-2 flex-1">{r.desc}</p>
            <Button data-testid={`download-${r.entity}-csv`} onClick={() => dlCsv(r.entity)}
                    variant="outline" className="mt-4 w-full">
              <Download size={15} className="mr-2"/> Download {r.entity}.csv
            </Button>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
